import React, { useMemo, useEffect, useState } from 'react'
import { Select, Tree, Tooltip } from 'antd'
import floder_icon from '../../../../images/folder_icon.png'
import naming_icon from '../../../../images/example.png'
import level1_name from '../assets/level_1_name.png'
import level1_date from '../assets/level_1_date.png'
import level1_location from '../assets/level_1_location.png'
import level1_watermark from '../assets/level_1_watermark.png'
import level1_item from '../assets/level_1_item.png'
import level1_unclassified from '../assets/level_1_unclassified.png'
import level2_img from '../assets/level_2.png'
import level3_img from '../assets/level_3.png'
import vip_icon from '../assets/vip_icon.png'
import { useVip } from '../../../../app/hooks'
import { CaretDownOutlined, CloseCircleOutlined } from '@ant-design/icons'

const Option = Select.Option

type FolderLevelItem = {
  level: number
  field?: string | undefined
}

interface ContentProps {
  folderLevels?: FolderLevelItem[]
  photoTitleList?: any[]
  sampleFiles?: any[]
  visible?: boolean
  onChangeLevel?: (level: number, value: any) => void
  isVip?: boolean
  multiTrialLeft?: number
  track?: (source: string, params?: any) => void
}

const Content: React.FC<ContentProps> = ({
  folderLevels = [
    { level: 1, field: '' },
    { level: 2, field: undefined },
    { level: 3, field: undefined }
  ],
  photoTitleList = [],
  sampleFiles = [],
  visible,
  onChangeLevel,
  isVip,
  multiTrialLeft,
  track
}) => {
  const { isVip: hookIsVip, isSVip, isVVip } = useVip()
  const isBasicMember = !!hookIsVip && !isSVip && !isVVip
  const normalizedFolderLevels = Array.isArray(folderLevels)
    ? folderLevels
    : [
        { level: 1, field: '' },
        { level: 2, field: undefined },
        { level: 3, field: undefined }
      ]
  const presetOptions = useMemo(
    () => [
      { key: 'user', label: '拍摄人姓名' },
      { key: 'date', label: '拍摄日期' },
      { key: 'address', label: '拍摄地点' },
      { key: 'watermark', label: '水印名称' },
      { key: 'photoNumber', label: '照片编号' }
    ],
    []
  )

  const previewMap = useMemo(
    () => ({
      user: level1_name,
      date: level1_date,
      address: level1_location,
      watermark: level1_watermark,
      photoNumber: level1_unclassified
    }),
    []
  )

  const FolderSvg = () => (
    <svg
      viewBox="0 0 1024 1024"
      width="14"
      height="14"
      style={{
        marginRight: 8,
        display: 'inline-block',
        verticalAlign: 'middle'
      }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M918.673 883H104.327C82.578 883 65 867.368 65 848.027V276.973C65 257.632 82.578 242 104.327 242h814.346C940.422 242 958 257.632 958 276.973v571.054C958 867.28 940.323 883 918.673 883z"
        fill="#FFE9B4"
        p-id="6674"
      ></path>
      <path
        d="M512 411H65V210.37C65 188.597 82.598 171 104.371 171h305.92c17.4 0 32.71 11.334 37.681 28.036L512 411z"
        fill="#FFB02C"
        p-id="6675"
      ></path>
      <path
        d="M918.673 883H104.327C82.578 883 65 865.42 65 843.668V335.332C65 313.58 82.578 296 104.327 296h814.346C940.422 296 958 313.58 958 335.332v508.336C958 865.32 940.323 883 918.673 883z"
        fill="#FFCA28"
        p-id="6676"
      ></path>
    </svg>
  )

  const handleChange = (levelIndex, value) => {
    if (onChangeLevel) onChangeLevel(levelIndex + 1, value)

    // 上报拍摄地点限制（不包含 namingMethod）
    if (value === 'address' && (isBasicMember || !isVip) && track) {
      track('拍摄地点限制', {
        level: levelIndex + 1
      })
    }
  }

  // 清理上次缓存的无效选择：
  // - 如果是 title:xxxx，但当前 photoTitleList 中没有对应的 titleId，则清空该层的选择
  useEffect(() => {
    if (!onChangeLevel) return
    const allowedPresetKeys = presetOptions.map((o) => o.key)
    const titleIdSet = new Set(
      (photoTitleList || []).map((it: any) => String(it && it.titleId))
    )

    ;(Array.isArray(folderLevels)
      ? folderLevels
      : normalizedFolderLevels
    ).forEach((lv, idx) => {
      if (!lv) return
      const f = lv.field
      if (typeof f !== 'string') return

      // title: 前缀判断
      if (f.startsWith('title:')) {
        const id = f.slice('title:'.length)
        if (!titleIdSet.has(String(id))) {
          const clearVal = idx === 0 ? '' : undefined
          onChangeLevel(idx + 1, clearVal)
        }
        return
      }

      // 一级默认空字符串
      if (idx === 0) {
        if (f !== '' && !allowedPresetKeys.includes(f)) {
          onChangeLevel(1, '')
        }
      } else {
        if (f !== '' && !allowedPresetKeys.includes(f)) {
          onChangeLevel(idx + 1, undefined)
        }
      }
    })
  }, [photoTitleList, folderLevels, presetOptions, onChangeLevel])

  const FolderLevel = ({ levelIndex }: { levelIndex: number }) => {
    const level = normalizedFolderLevels[levelIndex] || { field: undefined }
    // 兼容旧版本
    if (level.level !== 1 && level.field === '') {
      level.field = undefined
    }
    if (level.level === 1 && level.field === 'customize') {
      level.field = ''
    }
    const prevLevel =
      levelIndex > 0 ? normalizedFolderLevels[levelIndex - 1] : null
    const enabled =
      levelIndex === 0 ||
      (prevLevel &&
        prevLevel.field !== undefined &&
        prevLevel.field !== '' &&
        prevLevel.field !== null)

    const levelClass = `folder-level ${
      level && level.field === 'address' && (isBasicMember || !isVip)
        ? 'no-gap'
        : ''
    }`
    return (
      <>
        <div className={levelClass} key={`level-${levelIndex + 1}`}>
          <div className="folder-level__title">
            <div className="folder-level__title-left">
              <span className="folder-level__label">
                {levelIndex === 0
                  ? '一级文件夹'
                  : levelIndex === 1
                    ? '二级文件夹(可选)'
                    : '三级文件夹(可选)'}
              </span>
              {levelIndex > 0 && (
                <img className="folder-level__vip" src={vip_icon} alt="" />
              )}
            </div>
          </div>

          <div
            className={`folder-level__control ${!enabled ? 'is-disabled' : ''}`}
          >
            <div className="folder-level__icon">
              <img src={floder_icon} alt="" />
            </div>
            <Select
              className={`folder-level__select ${
                !enabled ? 'folder-level__select--disabled' : ''
              }`}
              value={level.field !== undefined ? level.field : undefined}
              onChange={(val) => handleChange(levelIndex, val)}
              allowClear={levelIndex > 0}
              clearIcon={
                levelIndex > 0 ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: '100%',
                      paddingRight: 6
                    }}
                  >
                    <CloseCircleOutlined
                      style={{
                        fontSize: 16,
                        color: '#1890ff',
                        lineHeight: '1',
                        marginTop: 2
                      }}
                    />
                  </span>
                ) : undefined
              }
              disabled={!enabled}
              suffixIcon={<CaretDownOutlined />}
              placeholder={levelIndex === 0 ? '默认文件夹（不归类）' : '请选择'}
            >
              {levelIndex === 0 && (
                <Option key="__default" value="">
                  默认文件夹（不归类）
                </Option>
              )}
              {presetOptions
                .filter(
                  (opt) => !(levelIndex === 0 && opt.key === 'photoNumber')
                )
                .map((opt) => {
                  const alreadyUsed = normalizedFolderLevels.some(
                    (lv, idx) =>
                      lv && lv.field === opt.key && idx !== levelIndex
                  )
                  return (
                    <Option
                      key={opt.key}
                      value={opt.key}
                      disabled={alreadyUsed}
                    >
                      <Tooltip title={alreadyUsed ? '不可重复选择' : null}>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}
                        >
                          {opt.label}
                        </span>
                      </Tooltip>
                    </Option>
                  )
                })}
              {photoTitleList.map((item: any) => {
                const val = `title:${item.titleId}`
                const alreadyUsed = normalizedFolderLevels.some(
                  (lv, idx) => lv && lv.field === val && idx !== levelIndex
                )
                return (
                  <Option key={val} value={val} disabled={alreadyUsed}>
                    <Tooltip title={alreadyUsed ? '不可重复选择' : null}>
                      <span>{item.titleName}</span>
                    </Tooltip>
                  </Option>
                )
              })}
            </Select>
          </div>
        </div>
        {level.field === 'address' &&
          (isBasicMember || !isVip) &&
          levelIndex === 0 &&
          typeof multiTrialLeft !== 'undefined' && (
            <div className="vip-note">
              拍摄地点为高级会员功能
              {isBasicMember && multiTrialLeft > 0 ? '，可免费试用' : ''}
            </div>
          )}
      </>
    )
  }

  // 构建树形结构数据
  const buildTreeDataFromSamples = (files: any[]) => {
    const rootMap = new Map<string, any>()

    for (const f of files || []) {
      const folderV2 = Array.isArray(f.folderV2) ? f.folderV2 : []
      if (folderV2.length === 0) {
        const key = '__unclassified'
        if (!rootMap.has(key))
          rootMap.set(key, { __children: new Map(), __files: [] })
        rootMap.get(key).__files.push(f)
        continue
      }
      let cur = rootMap
      for (let i = 0; i < folderV2.length; i++) {
        const name = folderV2[i] || ''
        if (!cur.has(name))
          cur.set(name, { __children: new Map(), __files: [] })
        const node = cur.get(name)
        if (i === folderV2.length - 1) {
          node.__files.push(f)
        }
        cur = node.__children
      }
    }

    const build = (map: Map<string, any>, prefix = ''): any[] => {
      const res: any[] = []
      map.forEach((val, key) => {
        const titleText =
          key === '__unclassified' ? '默认文件夹（不归类）' : key
        const nodeKey = prefix ? `${prefix}||${key}` : key
        const children = build(val.__children, nodeKey)

        const folderTitle = (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <FolderSvg />
            <span>
              {titleText.length > 20
                ? titleText.slice(0, 20) + '...'
                : titleText}
            </span>
          </span>
        )

        res.push({
          title: folderTitle,
          key: nodeKey,
          path: nodeKey.split('||').filter((p) => p !== ''),
          files: val.__files,
          children: children
        })
      })
      return res
    }

    return build(rootMap)
  }

  // 计算预览图片和 class
  const previewClass = useMemo(() => {
    return normalizedFolderLevels &&
      normalizedFolderLevels[2] &&
      normalizedFolderLevels[2].field !== undefined &&
      normalizedFolderLevels[2].field !== ''
      ? 'preview-level-3'
      : normalizedFolderLevels &&
          normalizedFolderLevels[1] &&
          normalizedFolderLevels[1].field !== undefined &&
          normalizedFolderLevels[1].field !== ''
        ? 'preview-level-2'
        : normalizedFolderLevels &&
            normalizedFolderLevels[0] &&
            normalizedFolderLevels[0].field !== undefined &&
            normalizedFolderLevels[0].field !== ''
          ? 'preview-level-1'
          : 'preview-level-1 no-preview-img'
  }, [normalizedFolderLevels])

  const previewSrc = useMemo(() => {
    if (
      normalizedFolderLevels &&
      normalizedFolderLevels[2] &&
      normalizedFolderLevels[2].field !== undefined &&
      normalizedFolderLevels[2].field !== ''
    ) {
      return level3_img
    } else if (
      normalizedFolderLevels &&
      normalizedFolderLevels[1] &&
      normalizedFolderLevels[1].field !== undefined &&
      normalizedFolderLevels[1].field !== ''
    ) {
      return level2_img
    } else if (normalizedFolderLevels && normalizedFolderLevels[0]) {
      const f = normalizedFolderLevels[0].field
      if (!f) return level1_unclassified
      if (typeof f === 'string' && f.startsWith('title:')) return level1_item
      return previewMap[f] || level1_unclassified
    }
    return naming_icon
  }, [normalizedFolderLevels, previewMap])

  // 构建树形结构数据
  const treeData = useMemo(
    () => buildTreeDataFromSamples(sampleFiles),
    [sampleFiles]
  )
  const [expandedKeys, setExpandedKeys] = useState<string[] | undefined>(
    undefined
  )
  // 在弹窗打开时开始计时；初始图片3s 后允许展示
  const [suppressPreview, setSuppressPreview] = useState(true)
  useEffect(() => {
    let tid: number | undefined
    if (visible) {
      tid = window.setTimeout(() => {
        setSuppressPreview(false)
      }, 3000)
    } else {
      setSuppressPreview(true)
    }
    return () => {
      if (tid) window.clearTimeout(tid)
    }
  }, [visible])

  // 当treeData变化时，默认展开所有节点
  useEffect(() => {
    if (!Array.isArray(treeData) || treeData.length === 0) {
      setExpandedKeys(undefined)
      return
    }
    // 收集所有节点key
    const collectKeys = (nodes: any[]): string[] => {
      let res: string[] = []
      for (const n of nodes) {
        if (n && n.key) res.push(String(n.key))
        if (Array.isArray(n.children) && n.children.length > 0) {
          res = res.concat(collectKeys(n.children))
        }
      }
      return res
    }
    const allKeys = collectKeys(treeData)
    setExpandedKeys(allKeys)
  }, [treeData])

  return (
    <div className="folder-classify">
      <div className="folder-card">
        <div className="folder-card__header">
          <span className="title">照片分类保存</span>
          {isBasicMember &&
            typeof multiTrialLeft !== 'undefined' &&
            multiTrialLeft > 0 && (
              <span className="trial-text">
                高级下载试用 {10 - multiTrialLeft || 0}/10
              </span>
            )}
        </div>

        <div className="folder-card__body">
          <div className="folder-left">
            <FolderLevel levelIndex={0} />
            <FolderLevel levelIndex={1} />
            <FolderLevel levelIndex={2} />
          </div>
          <div className="folder-right">
            <div className="preview-card">
              <div className="preview-card__title">
                下载预览
                <span className="preview-card__title-text">
                  仅展示前 100 张照片的文件夹，下载后获取完整目录
                </span>
              </div>
              <div className="preview-card__box">
                {(Array.isArray(treeData) && treeData.length > 0) ||
                suppressPreview ? (
                  <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                    <Tree
                      treeData={treeData}
                      expandedKeys={expandedKeys}
                      onExpand={(keys) =>
                        setExpandedKeys(keys as any as string[])
                      }
                      defaultExpandAll
                    />
                  </div>
                ) : (
                  <div className="preview-card__img">
                    <img className={previewClass} src={previewSrc} alt="示例" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Content
